import { useExchangeRate } from '../../hooks/useExchangeRate'
import { estimateCad, isForeign } from '../../utils/expenseMoney'

function money(n) {
    return (Number(n) || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })
}

function shortDate(iso) {
    const [y, m, d] = String(iso).split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
}

/**
 * "USD 45.00 · CAD estimated" under a saved line's amount, so a foreign line
 * reads as one at a glance and an unconfirmed estimate stands out.
 */
export function ForeignAmountNote({ expense, className = '' }) {
    if (!expense.currency || expense.currency === 'CAD') return null
    return (
        <span className={`text-xs whitespace-nowrap ${expense.cadEstimated ? 'text-amber-700 font-medium' : 'text-gray-500'} ${className}`}>
            {expense.currency} {Number(expense.originalAmount || 0).toFixed(2)}
            {expense.cadEstimated ? ' · CAD estimated' : ''}
        </span>
    )
}

/**
 * Amount paid, the currency it is in, and the HST box.
 *
 * A Canadian receipt is one number - what went on the card. A foreign one is
 * typed as printed (USD 45.00); the CAD the card was charged is not known until
 * the statement, so it is estimated from the Bank of Canada rate and marked as
 * an estimate until someone enters the statement figure. No HST or GST is
 * claimable on a foreign receipt, so the HST box goes away for one.
 *
 * value/onChange carry the whole line: currency, originalAmount, amountPaid
 * (always CAD), cadConfirmed and hasTax.
 */
export default function AmountField({ value, onChange, currencies = [], help = true, required = true }) {
    const foreign = isForeign(value)
    const fx = useExchangeRate(foreign ? value.currency : null, value.expenseDate)
    const estimate = foreign ? estimateCad(value.originalAmount, fx.rate, fx.markup) : 0

    function setCurrency(currency) {
        // The number in the box stays put - only what it means changes.
        const typed = foreign ? value.originalAmount : value.amountPaid
        onChange(currency === 'CAD'
            ? { ...value, currency, amountPaid: typed, originalAmount: '', cadConfirmed: false, hasTax: true }
            : { ...value, currency, originalAmount: typed, amountPaid: '', cadConfirmed: false, hasTax: false })
    }

    return (
        <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:items-end">
                <div>
                    <label className="form-label">Amount paid{required ? ' *' : ''}</label>
                    <div className="flex gap-2">
                        <select
                            value={value.currency || 'CAD'}
                            onChange={e => setCurrency(e.target.value)}
                            style={{ width: 'auto' }}
                            title="The currency the receipt is printed in"
                        >
                            <option value="CAD">CAD</option>
                            {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={(foreign ? value.originalAmount : value.amountPaid) ?? ''}
                            onChange={e => onChange(foreign
                                ? { ...value, originalAmount: e.target.value }
                                : { ...value, amountPaid: e.target.value })}
                            placeholder="0.00"
                        />
                    </div>
                    {help && (
                        <p className="text-xs text-gray-500 mt-1">
                            {foreign
                                ? `The total exactly as printed on the receipt, in ${value.currency}.`
                                : 'The total on the receipt, exactly as charged to the card.'}
                        </p>
                    )}
                </div>

                {!foreign && (
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                            type="checkbox"
                            className="w-4 h-4"
                            style={{ width: 'auto' }}
                            checked={value.hasTax}
                            onChange={e => onChange({ ...value, hasTax: e.target.checked })}
                        />
                        HST was charged
                    </label>
                )}
            </div>

            {foreign && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm space-y-2">
                    {!value.cadConfirmed && (
                        fx.loading ? (
                            <p className="text-gray-600">Looking up the exchange rate…</p>
                        ) : fx.unavailable ? (
                            <p className="text-amber-900">
                                The Bank of Canada rate could not be fetched just now. Tick the box below and
                                enter the CAD amount from your card statement.
                            </p>
                        ) : (
                            <p className="text-amber-900">
                                About <strong>{money(estimate)}</strong>
                                {fx.rate && <> at {Number(fx.rate).toFixed(4)} (Bank of Canada, {shortDate(fx.asOf)})</>}.
                                {' '}Saved as an estimate — the office corrects it from the card statement.
                            </p>
                        )
                    )}

                    <label className="flex items-center gap-2 font-medium text-gray-700">
                        <input
                            type="checkbox"
                            style={{ width: 'auto' }}
                            checked={value.cadConfirmed === true}
                            onChange={e => onChange({
                                ...value,
                                cadConfirmed: e.target.checked,
                                // Start from the estimate - usually only a few cents off.
                                amountPaid: e.target.checked && !value.amountPaid && estimate > 0
                                    ? estimate.toFixed(2) : value.amountPaid,
                            })}
                        />
                        I have the CAD amount from the card statement
                    </label>

                    {value.cadConfirmed && (
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={value.amountPaid ?? ''}
                            onChange={e => onChange({ ...value, amountPaid: e.target.value })}
                            placeholder="CAD on the statement"
                        />
                    )}

                    <p className="text-xs text-gray-600">
                        No HST or GST is claimed on a foreign receipt — the whole amount is the expense.
                    </p>
                </div>
            )}
        </div>
    )
}
